/**
 * The checkout's payment session, started the moment the checkout's HTML
 * arrives instead of after the app has booted.
 *
 * The partner's card form can only start loading once a payment session
 * exists, and the session takes about a second to create. The checkout used
 * to create it after the app's JavaScript downloaded and booted and the
 * product loaded (1.3s later on 4G, more on slow phones). A tiny separate
 * entry (src/checkoutEarly.ts, loaded by index.html ahead of the app) now
 * creates it right away for a plain checkout URL, with the buyer currency
 * resolved by the API from the request's IP (`currency: "auto"`). The
 * checkout then takes that session instead of creating its own, when it is
 * the session it would have created (same product, metadata and currency);
 * otherwise it creates its own as before.
 *
 * Kept dependency-free (fetch, no axios / app code) so the entry is a few KB.
 */

import {
	abVisitorBody,
	getAbVisitorId,
	renderedAbVariant,
} from "src/lib/abVisitor";
import { buyerTimezone } from "src/lib/buyerTimezone";
import { trackedLinkBody } from "src/lib/trackedLink";
import { visitSourcePayload } from "src/lib/visitSource";
import { DEVICE_ID_HEADER, getDeviceId } from "src/services/deviceId";
import { serializeWebhookMetadata } from "./checkoutWebhookParams";
import { warmPartnerSandbox } from "./partnerSandboxWarm";

export type EarlyIntentRequest = {
	productId: string;
	webhookMetadata: string;
	/** A currency carried from the product page (`?c=`), else "auto". */
	currency: string;
	/** Prepared ahead of the click: the API's prewarm rate-limit bucket. */
	prewarm: boolean;
	/**
	 * A/B tests: the variant the product page last rendered for this product
	 * ("" = none). The checkout only takes this session when it renders the
	 * same one.
	 */
	abVariant: string;
};

// Params that change what the checkout creates (a variant, a discount, a
// pay-what-you-want price, a subscription, a hosted session) or mean the
// session already exists: those checkouts create their own, as before.
const NOT_PLAIN = [
	"subscriptionId",
	"variantId",
	"discount",
	"pw",
	"direct",
	"checkout_session_id",
	"redirect_status",
	// Personal deadline link: priced per visitor, the checkout creates it.
	"pf_sid",
];

const OBJECT_ID = /^[a-f0-9]{24}$/i;

/** The early session for this URL, or null when it is not a plain checkout. */
export const earlyIntentRequest = (
	location: Pick<Location, "pathname" | "search">,
): EarlyIntentRequest | null => {
	if (location.pathname.replace(/\/+$/, "") !== "/checkout") return null;
	const params = new URLSearchParams(location.search);
	const productId = params.get("productId");
	if (!productId || !OBJECT_ID.test(productId)) return null;
	if (NOT_PLAIN.some((key) => params.has(key))) return null;
	return {
		productId,
		webhookMetadata: serializeWebhookMetadata(params),
		currency: params.get("c") || "auto",
		prewarm: params.get("prewarm") === "1",
		abVariant: renderedAbVariant(productId),
	};
};

export type EarlyIntent = { id: string; [key: string]: any };

// The API records the charge currency in the session's metadata.
const chargeCurrencyOf = (data: EarlyIntent) =>
	String(data.metadata?.currency || data.currency || "").toUpperCase();

let pending: {
	request: EarlyIntentRequest;
	intent: Promise<EarlyIntent | null>;
} | null = null;

const SESSION_TIMEOUT_MS = 8_000;

/** Starts the early session for the current URL (once per page). */
export const startEarlyCheckoutIntent = (
	win: Window = window,
	apiBase: string = String(import.meta.env.VITE_APP_API_SERVER_URL ?? ""),
) => {
	// No API origin configured: same origin, like the app's API client.
	if (pending) return;
	// An identified personal-deadline visitor (pf_sid cookie) is priced per
	// person: the checkout creates that session itself, with the token.
	try {
		if (/(?:^|;\s*)pf_sid=/.test(win.document?.cookie ?? "")) return;
	} catch {
		// Cookies unreadable: carry on with the early session.
	}
	const request = earlyIntentRequest(win.location);
	if (!request) return;
	const controller =
		typeof AbortController === "undefined" ? null : new AbortController();
	const timer = setTimeout(() => controller?.abort(), SESSION_TIMEOUT_MS);
	const intent = fetch(
		`${apiBase.replace(/\/+$/, "")}/products/create-whop-payment-intent`,
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				[DEVICE_ID_HEADER]: getDeviceId(),
			},
			body: JSON.stringify({
				productId: request.productId,
				payWantPrice: 0,
				currency: request.currency,
				webhookMetadata: request.webhookMetadata,
				...(request.prewarm ? { prewarm: true } : {}),
				// Where the visit came from, for Revenue by source (server
				// resolves it; nothing personal is sent).
				visit: visitSourcePayload(),
				// Starter `trackedLinks`: the creator's short link, if any.
				...trackedLinkBody(),
				// A/B tests (Pro): the visitor id the server prices the
				// session from (same id the checkout page will use).
				...abVisitorBody(),
				abVariant: request.abVariant,
				// Launch pricing: the browser time zone (fair-pricing check).
				timezone: buyerTimezone(),
			}),
			signal: controller?.signal,
		},
	)
		.then(async (res) => {
			if (!res.ok) return null;
			const data = await res.json();
			// A free product answers `{ free: true }`: no session to take.
			if (!data || typeof data.id !== "string") return null;
			if (data.metadata?.testMode === true) noteTestMode(win.document);
			return data as EarlyIntent;
		})
		.catch(() => null)
		.finally(() => clearTimeout(timer));
	pending = { request, intent };
	// The product, fetched alongside: the checkout reads it from here
	// (storefront/queries.ts) instead of fetching it after it boots.
	const product = fetch(
		`${apiBase.replace(/\/+$/, "")}/products/prod/${request.productId}`,
		{ headers: { [DEVICE_ID_HEADER]: getDeviceId() } },
	)
		.then((res) => (res.ok ? res.json() : null))
		.then((data) => {
			// Test mode: the payment frame loads from the partner's sandbox.
			// Open that connection now, while the session is still being
			// created (partnerSandboxWarm.ts).
			if (data?.testMode === true) noteTestMode(win.document);
			return data;
		})
		.catch(() => null);
	earlyProduct = { productId: request.productId, product };
	startEarlyAbAssignment(win, apiBase, request.productId);
};

// ── A/B assignment, asked alongside the session ─────────────────────────────
//
// The checkout holds its first paint until it knows this visitor's A/B
// variant (useAbTest.ts). Asked after the app boots, that lookup alone kept
// the product off screen for ~1 s on a measured live checkout (2026-10-31:
// the answer came at 4.0 s, the product appeared at 4.2 s). Asked here, it
// runs while the app downloads; the checkout's first lookup takes it.

/** Same as useAbTest.ts: a slow lookup gives up and shows the product as is. */
const AB_ASSIGN_TIMEOUT_MS = 2_500;

/** `undefined` = the lookup failed (the checkout then asks itself). */
type EarlyAbAnswer = { test: unknown } | undefined;

let earlyAbAssignment: {
	productId: string;
	visitorId: string;
	answer: Promise<EarlyAbAnswer>;
} | null = null;

const startEarlyAbAssignment = (
	win: Window,
	apiBase: string,
	productId: string,
) => {
	const visitorId = getAbVisitorId(win);
	if (!visitorId) return;
	const controller =
		typeof AbortController === "undefined" ? null : new AbortController();
	const timer = setTimeout(() => controller?.abort(), AB_ASSIGN_TIMEOUT_MS);
	const answer = fetch(
		`${apiBase.replace(/\/+$/, "")}/ab-tests/public/assign`,
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				[DEVICE_ID_HEADER]: getDeviceId(),
			},
			body: JSON.stringify({ productId, visitorId }),
			signal: controller?.signal,
		},
	)
		.then(async (res): Promise<EarlyAbAnswer> => {
			if (!res.ok) return undefined;
			const data = await res.json();
			return { test: data?.test ?? null };
		})
		.catch((): EarlyAbAnswer => undefined)
		.finally(() => clearTimeout(timer));
	earlyAbAssignment = { productId, visitorId, answer };
};

/**
 * The A/B answer asked with the early session, for the checkout's first
 * lookup of this product by this visitor (in the seller's currency: a
 * currency-specific lookup asks the API itself). Handed out once. Resolves
 * to undefined when the early lookup failed.
 */
export const takeEarlyAbAssignment = (
	productId: unknown,
	visitorId: string | null,
): Promise<EarlyAbAnswer> | null => {
	if (
		!earlyAbAssignment ||
		earlyAbAssignment.productId !== productId ||
		earlyAbAssignment.visitorId !== visitorId
	) {
		return null;
	}
	const { answer } = earlyAbAssignment;
	earlyAbAssignment = null;
	return answer;
};

let earlyTestMode = false;
const noteTestMode = (doc: Document | undefined) => {
	if (earlyTestMode) return;
	earlyTestMode = true;
	warmPartnerSandbox(doc);
	// The classic embed's code, which test mode pays through (the route
	// list carries the Elements island instead).
	if (doc) preloadCheckoutRoute(doc, CHECKOUT_SANDBOX_PRELOADS_ID);
};

/**
 * True once the early product (or session) said this checkout is in test
 * mode, before the checkout's own product query has it. Lets the checkout
 * pick the sandbox payment form's code from its first render.
 */
export const earlyCheckoutTestMode = (): boolean => earlyTestMode;

let earlyProduct: {
	productId: string;
	product: Promise<Record<string, unknown> | null>;
} | null = null;

/**
 * The product fetched with the early session, for its product's first
 * fetch only (later fetches go to the API as usual). Resolves to null when
 * that fetch failed: the caller then fetches it itself.
 */
export const takeEarlyProduct = (
	productId: string,
): Promise<Record<string, unknown> | null> | null => {
	if (!earlyProduct || earlyProduct.productId !== productId) return null;
	const { product } = earlyProduct;
	earlyProduct = null;
	return product;
};

/**
 * The early session, for the checkout's first create, when it is the one the
 * checkout would create: same product and metadata, no variant / discount /
 * pay-what-you-want price. Resolves to null when there is none, it failed,
 * or it is in another currency; the checkout then creates its own. Handed
 * out once.
 */
export const claimEarlyCheckoutIntent = (cart: {
	productId?: string | null;
	webhookMetadata: string;
	currency: string;
	variantId?: string | null;
	discountId?: string | null;
	payWantPrice?: number | string | null;
	autoRefillOptIn?: boolean;
	/** The A/B variant the checkout renders ("" = the product as it is). */
	abVariant?: string | null;
}): Promise<EarlyIntent | null> | null => {
	if (!pending) return null;
	const { request, intent } = pending;
	if (
		cart.productId !== request.productId ||
		cart.webhookMetadata !== request.webhookMetadata ||
		cart.variantId ||
		cart.discountId ||
		Number(cart.payWantPrice || 0) !== 0 ||
		cart.autoRefillOptIn ||
		// Priced for another A/B variant than the one on screen.
		(cart.abVariant ?? "") !== request.abVariant
	) {
		return null;
	}
	pending = null;
	return intent.then((data) =>
		data && chargeCurrencyOf(data) === String(cart.currency || "").toUpperCase()
			? data
			: null,
	);
};

/**
 * The currency the early session charges, without claiming it (#2917). The
 * server priced it from the buyer's IP, the same thing the checkout's own
 * geo lookup does, and the session cannot be re-priced: when it answers
 * first, the checkout shows that currency and claims it at once instead of
 * waiting up to the geo budget (median +530 ms before the claim, and a
 * re-mint whenever the two disagreed). Null when there is no early session
 * for this product; the promise resolves null when it failed.
 */
export const peekEarlyCheckoutIntentCurrency = (
	productId?: string | null,
): Promise<string | null> | null => {
	if (!pending || !productId || pending.request.productId !== productId) {
		return null;
	}
	return pending.intent.then((data) =>
		data ? chargeCurrencyOf(data) || null : null,
	);
};

/** Test-only. */
export const resetEarlyCheckoutIntent = () => {
	pending = null;
	earlyProduct = null;
	earlyTestMode = false;
	earlyAbAssignment = null;
};

/** Where index.html lists the checkout route's files (vite.config.ts). */
export const CHECKOUT_PRELOADS_ID = "pf-checkout-preloads";
/** Where index.html lists test mode's payment form code (vite.config.ts). */
export const CHECKOUT_SANDBOX_PRELOADS_ID = "pf-checkout-sandbox-preloads";

/**
 * On a checkout URL, starts downloading the checkout route's code right
 * away, alongside the app bundle. Without it that code only starts after
 * the app has booted and the router has rendered: on a slow phone several
 * round trips plus idle bandwidth. Preloads never execute anything.
 *
 * `listId` picks the list: the route itself, or (once the early product says
 * test mode) the classic embed that test mode pays through.
 */
export const preloadCheckoutRoute = (
	doc: Document = document,
	listId: string = CHECKOUT_PRELOADS_ID,
) => {
	const path = doc.location?.pathname ?? "";
	if (!/^\/checkout(\/|$)/.test(path) || /^\/checkout\/success/.test(path)) {
		return;
	}
	let files: unknown;
	try {
		files = JSON.parse(doc.getElementById(listId)?.textContent || "[]");
	} catch {
		return;
	}
	if (!Array.isArray(files)) return;
	for (const file of files) {
		if (typeof file !== "string" || !/^assets\/[\w.-]+\.(js|css)$/.test(file)) {
			continue;
		}
		const link = doc.createElement("link");
		if (file.endsWith(".css")) {
			link.rel = "preload";
			link.setAttribute("as", "style");
		} else {
			link.rel = "modulepreload";
		}
		// Same mode as the app's own loader, so its request reuses this one.
		link.crossOrigin = "";
		link.href = `/${file}`;
		doc.head.appendChild(link);
	}
};
